import React from 'react';
import styled from 'styled-components';

const HeroSection = styled.section`
  height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: start;
  overflow: hidden;
  padding: 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    height: auto;
    padding: 40px 0;
  }
`;

const HeroContent = styled.div`
  padding: 60px 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-self: center;

  @media (max-width: 768px) {
    padding: 0 20px;
    text-align: center;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1.5rem;
  color: #333;
  font-weight: 700;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  margin-bottom: 3rem;
  color: #666;
  max-width: 500px;
  line-height: 1.5;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1.5rem;

  @media (max-width: 768px) {
    justify-content: center;
    flex-direction: column;
    align-items: center;
  }
`;

const StoreButton = styled.a`
  display: inline-block;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }

  img {
    height: 60px;
    width: auto;
    display: block;
  }

  &.primary {
    background-color: #20b2aa;
    color: white;

    &:hover {
      background-color: #008b8b;
    }
  }

  &.secondary {
    background-color: transparent;
    color: #20b2aa;
    border: 2px solid #20b2aa;

    &:hover {
      background-color: rgba(32, 178, 170, 0.1);
    }
  }
`;

const FilmReelContainer = styled.div`
  display: flex;
  gap: 30px;
  height: 100vh;
  padding: 0 40px 0 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

const FilmColumn = styled.div`
  flex: 1;
  height: 100%;
  overflow: hidden;
  position: relative;
  // border-radius: 15px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
`;

const FilmStrip = styled.div`
  display: flex;
  flex-direction: column;
  animation: ${props => props.direction === 'up' ? 'scrollUp' : 'scrollDown'} 90s linear infinite;

  @keyframes scrollUp {
    from { transform: translateY(0); }
    to { transform: translateY(-50%); }
  }

  @keyframes scrollDown {
    from { transform: translateY(-50%); }
    to { transform: translateY(0); }
  }
`;

const FilmFrame = styled.div`
  width: 100%;
  height: 65vh;
  flex-shrink: 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 48%, rgba(255,255,255,0.1) 50%, transparent 52%);
    pointer-events: none;
    z-index: 1;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const FilmReel = () => {
  const travelImages = [
    "https://travelmaker-artefacts.s3.eu-north-1.amazonaws.com/city-fr-par_2.jpg",
    "https://travelmaker-artefacts.s3.eu-north-1.amazonaws.com/city-uk-lon_3.jpg",
    "https://travelmaker-artefacts.s3.eu-north-1.amazonaws.com/city-us-nyc_7.jpg",
    "https://travelmaker-artefacts.s3.eu-north-1.amazonaws.com/city-it-rom_11.jpg",
    "https://travelmaker-artefacts.s3.eu-north-1.amazonaws.com/city-jp-kyo_20.jpg",
    "https://travelmaker-artefacts.s3.eu-north-1.amazonaws.com/city-es-bcn_4.jpg",
    "https://travelmaker-artefacts.s3.eu-north-1.amazonaws.com/city-de-ber_3.jpg",
    "https://travelmaker-artefacts.s3.eu-north-1.amazonaws.com/city-nl-ams_10.jpg",
    "https://travelmaker-artefacts.s3.eu-north-1.amazonaws.com/city-at-vie_15.jpg",
    "https://travelmaker-artefacts.s3.eu-north-1.amazonaws.com/city-cn-pek_12.jpg",
    "https://travelmaker-artefacts.s3.eu-north-1.amazonaws.com/city-cz-prg_8.jpg",
    "https://travelmaker-artefacts.s3.eu-north-1.amazonaws.com/city-eg-cai_7.jpg",
    "https://travelmaker-artefacts.s3.eu-north-1.amazonaws.com/city-es-mad_9.jpg",
    "https://travelmaker-artefacts.s3.eu-north-1.amazonaws.com/city-gr-ath_6.jpg",
    "https://travelmaker-artefacts.s3.eu-north-1.amazonaws.com/city-it-ven_14.jpg",
    "https://travelmaker-artefacts.s3.eu-north-1.amazonaws.com/city-ma-rak_16.jpg",
    "https://travelmaker-artefacts.s3.eu-north-1.amazonaws.com/city-tr-ist_25.jpg",
    "https://travelmaker-artefacts.s3.eu-north-1.amazonaws.com/city-it-flo_21.jpg",
    "https://travelmaker-artefacts.s3.eu-north-1.amazonaws.com/city-es-grx_13.jpg",
    "https://travelmaker-artefacts.s3.eu-north-1.amazonaws.com/city-jp-kyo_15.jpg"
  ];

  const leftColumn = travelImages.slice(0, 10);
  const rightColumn = travelImages.slice(10, 20);

  return (
    <FilmReelContainer>
      <FilmColumn>
        <FilmStrip direction="up">
          {[...leftColumn, ...leftColumn].map((image, index) => (
            <FilmFrame key={`left-${index}`}>
              <img src={image} alt="Travel destination" />
            </FilmFrame>
          ))}
        </FilmStrip>
      </FilmColumn>
      <FilmColumn>
        <FilmStrip direction="down">
          {[...rightColumn, ...rightColumn].map((image, index) => (
            <FilmFrame key={`right-${index}`}>
              <img src={image} alt="Travel destination" />
            </FilmFrame>
          ))}
        </FilmStrip>
      </FilmColumn>
    </FilmReelContainer>
  );
};

const FeaturesSection = styled.section`
  background-color: #f9f9f9;
  padding: 80px 0;
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: 3rem;
  font-size: 2rem;
  color: #333;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const FeatureCard = styled.div`
  background-color: white;
  border-radius: 10px;
  padding: 2rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s;

  &:hover {
    transform: translateY(-5px);
  }
`;

const FeatureIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #20b2aa;
`;

const FeatureTitle = styled.h3`
  margin-bottom: 1rem;
  font-size: 1.5rem;
`;

const ScreenshotsSection = styled.section``;

const ScreenshotsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Screenshot = styled.div`
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: auto;
    display: block;
    transition: transform 0.3s;
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const CtaSection = styled.section`
  background-color: #20b2aa;
  color: white;
  text-align: center;
  padding: 80px 0;
`;

const CtaContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const CtaTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const CtaText = styled.p`
  margin-bottom: 2rem;
  opacity: 0.9;
`;

const CtaButton = styled.a`
  padding: 0.8rem 2rem;
  border-radius: 30px;
  font-weight: 600;
  transition: all 0.3s;
  display: inline-block;
  background-color: white;
  color: #20b2aa;

  &:hover {
    background-color: rgba(255, 255, 255, 0.9);
    transform: translateY(-3px);
  }
`;

const Home = () => {
  return (
    <main>
      <HeroSection>
        <HeroContent>
          <HeroTitle>Discover the World with TravelMaker</HeroTitle>
          <HeroSubtitle>
            Your personal travel companion that makes exploring new cities easy and enjoyable
          </HeroSubtitle>
          <ButtonGroup>
            <StoreButton href="https://apps.apple.com" target="_blank" rel="noopener noreferrer">
              <img src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" alt="Download on the App Store" />
            </StoreButton>
            <StoreButton href="https://play.google.com" target="_blank" rel="noopener noreferrer">
              <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Get it on Google Play" />
            </StoreButton>
          </ButtonGroup>
        </HeroContent>
        <FilmReel />
      </HeroSection>

      <FeaturesSection>
        <SectionTitle>Key Features</SectionTitle>
        <FeaturesGrid>
          <FeatureCard>
            <FeatureIcon>🗺️</FeatureIcon>
            <FeatureTitle>Interactive City Maps</FeatureTitle>
            <p>Explore cities with our interactive maps that highlight the best attractions, restaurants, and hidden gems.</p>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>🎧</FeatureIcon>
            <FeatureTitle>Audio Guides</FeatureTitle>
            <p>Listen to expert narrations about historical sites and cultural landmarks as you explore.</p>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>📝</FeatureIcon>
            <FeatureTitle>Personalized Itineraries</FeatureTitle>
            <p>Create custom travel plans based on your interests, time, and budget.</p>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>📸</FeatureIcon>
            <FeatureTitle>Augmented Reality</FeatureTitle>
            <p>Point your camera at landmarks to get instant information and historical context.</p>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>🌙</FeatureIcon>
            <FeatureTitle>Offline Mode</FeatureTitle>
            <p>Download city guides for offline use to avoid roaming charges while traveling.</p>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>💬</FeatureIcon>
            <FeatureTitle>Local Tips</FeatureTitle>
            <p>Get recommendations from locals about the best places to eat, shop, and explore.</p>
          </FeatureCard>
        </FeaturesGrid>
      </FeaturesSection>

      <ScreenshotsSection>
        <div className="container">
          <SectionTitle>App Screenshots</SectionTitle>
          <ScreenshotsGrid>
            <Screenshot>
              <img src="/images/screenshot1.jpg" alt="TravelMaker app screenshot - Map view" />
            </Screenshot>
            <Screenshot>
              <img src="/images/screenshot2.jpg" alt="TravelMaker app screenshot - Attraction details" />
            </Screenshot>
            <Screenshot>
              <img src="/images/screenshot3.jpg" alt="TravelMaker app screenshot - Audio guide" />
            </Screenshot>
            <Screenshot>
              <img src="/images/screenshot4.jpg" alt="TravelMaker app screenshot - Itinerary planner" />
            </Screenshot>
          </ScreenshotsGrid>
        </div>
      </ScreenshotsSection>

      <CtaSection>
        <CtaContent>
          <CtaTitle>Ready to Start Your Journey?</CtaTitle>
          <CtaText>
            Download TravelMaker today and transform the way you explore new destinations.
          </CtaText>
          <CtaButton href="https://apps.apple.com" target="_blank" rel="noopener noreferrer">
            Download Now
          </CtaButton>
        </CtaContent>
      </CtaSection>
    </main>
  );
};

export default Home;